<script>
  /* global Global, DEV_ENV */
  import Header from '../../common/Header.svelte';
  import Footer from '../../common/Footer.svelte';
  import Loading from '../../common/Loading.svelte';
  import Error from '../../common/Error.svelte';
  import Title from '../../common/Title.svelte';
  import Input from '../../common/Input.svelte';
  import Textarea from '../../common/Textarea.svelte';
  import Button from '../../common/Button.svelte';
  import MessageBox from '../../common/MessageBox.svelte';
  import Common from '../../js/common.js';
  import Validation from '../../js/validation.js';
  import ALL_PAGE_FIELDS from '../../js/fields/allPageFields.js';

  export let params;

  let lang;

  let user = JSON.parse(window.localStorage.getItem('user'));
  if (DEV_ENV) user = { firstName: 'Jan', lastName: 'Novák', roles: ['ROLE_ADMIN'] };
  if (!user.roles.includes('ROLE_ADMIN')) window.location.href = window.localStorage.getItem('previousPage') || './';

  let page;

  let loading = true;
  let error = false;

  (async () => {
    // eslint-disable-next-line no-undef
    const response = await fetch(`${Common.BACKEND_URL}/admin/pages/get/${params.page}`);

    if (response.ok) {
      const data = await response.json();

      page = data.page;
      user = data.user;

      if (user) {
        window.localStorage.setItem('user', JSON.stringify(data.user));
      } else {
        window.localStorage.removeItem('user');

        back();
      }

      Object.keys(page).forEach(key => {
        if (page[key] === null) {
          page[key] = undefined;
        } else if (typeof page[key] === 'object') {
          Object.keys(page[key]).forEach(nestedKey => {
            if (page[key][nestedKey] === null) page[key][nestedKey] = undefined;
          });
        }
      });
    } else {
      if (response.status === Common.NOT_FOUND || response.status === Common.UNAUTHORIZED) window.location.href = './';
      else error = true;
    }

    loading = false;
  })();

  let messageBox;
  let display = 'button';
  let errors = [];

  function editPage (validate = true) {
    display = 'loading';
    errors = [];

    messageBox = {
      cs: { color: 'blue', message: i18n.savingInProgress.cs },
      en: { color: 'blue', message: i18n.savingInProgress.en },
    };

    try {
      // eslint-disable-next-line no-undef
      const formData = new FormData();

      const data = Validation.convertAndCheck(ALL_PAGE_FIELDS, validate, page);

      formData.append('data', JSON.stringify(data));

      (async () => {
        // eslint-disable-next-line no-undef
        const response = await fetch(`${Common.BACKEND_URL}/admin/pages/edit/${params.page}`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const data = await response.text();

          messageBox = {
            cs: { color: 'green', message: `Úpravy stránky "${data}" byly úspěšně uloženy.` },
            en: { color: 'green', message: `Page "${data}" changes have been successfully saved.` },
          };

          display = 'saved';
        } else {
          messageBox = {
            cs: { color: 'red', message: i18n.unexpectedError.cs },
            en: { color: 'red', message: i18n.unexpectedError.en },
          };

          display = 'error';
        }
      })();
    } catch (error) {
      errors = error.errors;

      messageBox = {
        cs: { color: 'red', errors: errors.map(error => error.message.cs) },
        en: { color: 'red', errors: errors.map(error => error.message.en) },
      };

      display = 'error';
    }
  }

  function back () {
    window.location.href = window.localStorage.getItem('previousPage') || './';
  }

  async function translate (key) {
    // eslint-disable-next-line no-undef
    const response = await fetch(`${Common.BACKEND_URL}/translate`, {
      method: 'POST',
      body: page[key].cs,
    });

    if (response.ok) {
      page[key].en = await response.text();
    }
  }

  const i18n = {
    pageTitle: {
      cs: `Admin | ${Global.project.name.cs}`,
      en: `Admin | ${Global.project.name.en}`,
    },
    content: {
      cs: 'Obsah',
      en: 'Content',
    },
    label: {
      cs: 'Popisek',
      en: 'Label',
    },
    link: {
      cs: 'Odkaz',
      en: 'Link',
    },
    automaticallyTranslate: {
      cs: 'Automaticky přeložit',
      en: 'Automatically translate',
    },
    translateInProgress: {
      cs: 'Probíhá překlad...',
      en: 'Translate in progress...',
    },
    saveChanges: {
      cs: 'Uložit úpravy',
      en: 'Save changes',
    },
    back: {
      cs: 'Zpět',
      en: 'Back',
    },
    savingInProgress: {
      cs: 'Probíhá ukládání úprav...',
      en: 'Changes saving in progress...',
    },
    unexpectedError: {
      cs: '(Ne)očekávaná chyba. `E29`',
      en: '(Un)expected error. `E29`',
    },
    withoutValidation: {
      cs: 'bez validace',
      en: 'without validation',
    },
  };
</script>

<svelte:head>
  <title> {i18n.pageTitle[lang]} </title>
</svelte:head>

<Header page='admin/pages/{params.page}' bind:lang bind:user/>

<Title/>

<div class='admin-pages-page'>
  <Loading bind:display={loading}/>
  <Error bind:display={error} bind:lang/>

  {#if !loading && !error}
    <div class='admin-pages-page-box'>
      <!-- CONTENT CS-->
      <Textarea bind:value={page.content.cs} label='{i18n.content[lang]} [CS]' height='160px'
        error={errors.find(error => error.fieldName === 'content.cs')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)} actions={['bold', 'italic', 'link']}/>

      <!-- CONTENT EN -->
      <Textarea bind:value={page.content.en} label='{i18n.content[lang]} [EN]' height='160px'
        translate={i18n.automaticallyTranslate[lang]} onTranslateClick={() => { translate('content'); }}
        error={errors.find(error => error.fieldName === 'content.en')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)} actions={['bold', 'italic', 'link']}/>

      <!-- LABEL CS -->
      <Input bind:value={page.label.cs} label='{i18n.label[lang]} [CS]'
        error={errors.find(error => error.fieldName === 'label.cs')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- LABEL EN -->
      <Input bind:value={page.label.en} label='{i18n.label[lang]} [EN]'
        translate={i18n.automaticallyTranslate[lang]} onTranslateClick={() => { translate('label'); }}
        error={errors.find(error => error.fieldName === 'label.en')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      <!-- LINK -->
      <Input bind:value={page.link} label={i18n.link[lang]}
        error={errors.find(error => error.fieldName === 'link')?.message[lang]}
        disabled={['loading', 'saved'].includes(display)}/>

      {#if display === 'loading' || display === 'error'}
        <div class='admin-pages-page-box-message-box'>
          <MessageBox value={messageBox ? messageBox[lang] : undefined}/>
        </div>
      {/if}

      {#if display === 'button' || display === 'error'}
        <div class='admin-pages-page-box-button'>
          <Button value={i18n.saveChanges[lang]}
            onClick={() => { editPage(); }} icon='save' color='green'/>
        </div>
      {/if}

      {#if display === 'saved'}
        <div class='admin-pages-page-box-message-box-and-button'>
          <div class='admin-pages-page-box-message-box-and-button-message-box'>
            <MessageBox value={messageBox ? messageBox[lang] : undefined}/>
          </div>

          <div class='admin-pages-page-box-message-box-and-button-button'>
            <Button value={i18n.back[lang]} onClick={back} icon='arrow_back'
              cssClass='admin-pages-page-box-message-box-and-button-button-component'/>
          </div>
        </div>
      {/if}
    </div>

    <div class='admin-pages-page-right-panel'>
      <div class='admin-pages-page-right-panel-box'/>

      <div class='admin-pages-page-right-panel-save-without-validation'>
        <Button value={i18n.saveChanges[lang] + ' ' + i18n.withoutValidation[lang]}
          onClick={() => { editPage(false); }} icon='warning'/>
      </div>
    </div>
  {/if}
</div>

<Footer bind:lang/>

<style>
  .admin-pages-page {
    display: flex;
    justify-content: space-between;
    gap: 40px;

    max-width: 1440px;

    padding: 40px 60px;

    margin: auto;
  }

  .admin-pages-page-box {
    display: flex;
    flex-direction: column;
    gap: 40px;

    width: calc((100% - 40px) / 3 * 2);
  }

  .admin-pages-page-box-button {
    display: flex;
  }

  .admin-pages-page-box-message-box-and-button {
    display: flex;
    gap: 40px;
  }

  .admin-pages-page-box-message-box-and-button-message-box {
    width: calc((100% - 40px) / 3 * 2);
  }

  .admin-pages-page-box-message-box-and-button-button {
    width: calc((100% - 40px) / 3);
  }

  .admin-pages-page-right-panel {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-shrink: 0;
    gap: 40px;

    width: calc((100% - 40px) / 3);
  }

  .admin-pages-page-right-panel-box {
    height: 100%;

    padding: 20px;

    border-radius: 5px;

    background: var(--grey-1);
  }

  :global(.admin-pages-page-box-message-box-and-button-button-component) {
    height: 100% !important;
  }

  :global(body.dark) .admin-pages-page-right-panel-box {
    background: var(--grey-8);
  }

  @media only screen and (max-width: 1300px) {
    .admin-pages-page-box {
      width: 100%;
    }

    .admin-pages-page-right-panel {
      display: none;
    }
  }

  @media only screen and (max-width: 900px) {
    .admin-pages-page {
      padding: 40px;
    }

    .admin-pages-page-box-message-box-and-button {
      flex-direction: column;
      gap: 20px;
    }

    .admin-pages-page-box-message-box-and-button-message-box, .admin-pages-page-box-message-box-and-button-button {
      width: 100%;
    }

    :global(.admin-pages-page-box-message-box-and-button-button-component) {
      height: 40px !important;
    }
  }

  @media only screen and (max-width: 350px) {
    .admin-pages-page {
      padding: 40px 20px;
    }
  }
</style>
